import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMemo, useState, useEffect } from 'react';
import { DOCUMENTATION_DATA } from './documentationData';
import { buildSearchIndex, searchIndex } from './documentationSearch';

const SEARCH_INDEX = buildSearchIndex(DOCUMENTATION_DATA);

// Helper function to format text
const formatText = (text) => {
  if (!text) return '';

  return text
    .replace(/```([\s\S]*?)```/g, (_, code) => `<pre><code>${code}</code></pre>`) // Multiline code block
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
    .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italics
    .replace(/__(.*?)__/g, '<u>$1</u>') // Underline
    .replace(/`(.*?)`/g, '<code>$1</code>') // Inline code
    .replace(/\~\~(.*?)\~\~/g, '<del>$1</del>') // Strikethrough
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>') // Links
    .replace(/>(.*?)\n/g, '<blockquote>$1</blockquote>'); // Blockquote
};

const CodeBlock = ({ content, language = 'json' }) => (
  <Box
    component="pre"
    sx={{
      backgroundColor: '#1e1e1e',
      color: '#dcdcdc',
      p: 2,
      borderRadius: 2,
      overflowX: 'auto',
      fontSize: 14,
      lineHeight: 1.6,
      mt: 2,
      width: '70%',
    }}
  >
    <code>{content}</code>
  </Box>
);

const DocumentationSection = ({ data }) => {
  if (!data) {
    return (
      <Typography variant="h6" color="gray">
        Content not available
      </Typography>
    );
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      {/* Title */}
      <Typography
        variant="h5"
        fontWeight={600}
        gutterBottom
        dangerouslySetInnerHTML={{ __html: formatText(data.title) }}
      />

      {/* Description */}
      {data.description && (
        <Typography
          variant="body1"
          paragraph
          sx={{ lineHeight: 1.6 }}
          dangerouslySetInnerHTML={{ __html: formatText(data.description) }}
        />
      )}

      {/* Section-level Notes */}
      {data.note && (
        <Box
          mt={3}
          sx={{
            backgroundColor: '#f0f8ff',
            borderLeft: '5px solid #2196f3',
            display: 'flex',
            alignItems: 'center',
            p: 2,
            borderRadius: '8px',
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontWeight: 500 }}
            dangerouslySetInnerHTML={{ __html: formatText(data.note) }}
          />
        </Box>
      )}

      {/* Image */}
      {data.image && (
        <Box mt={4} sx={{ width: '100%', textAlign: 'center' }}>
          <img
            src={data.image}
            alt={data.title}
            style={{
              maxWidth: '700px',
              maxHeight: '400px',
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
        </Box>
      )}

      {/* Sections */}
      {data.sections?.map((section, index) => (
        <Box key={index} mt={3}>
          {section.heading && (
            <Typography
              variant="h6"
              fontWeight={500}
              color="primary"
              gutterBottom
              dangerouslySetInnerHTML={{ __html: formatText(section.heading) }}
            />
          )}

          {/* Section-level Image */}
          {section.image && (
            <Box mt={2} sx={{ width: '100%', textAlign: 'center' }}>
              <img
                src={section.image}
                alt={section.heading}
                style={{
                  maxWidth: '700px',
                  maxHeight: '400px',
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '8px',
                }}
              />
            </Box>
          )}

          {/* Paragraphs */}
          {section.paragraphs?.map((para, i) => (
            <Typography
              key={i}
              variant="body1"
              paragraph
              sx={{ lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: formatText(para) }}
            />
          ))}

          {/* Bullet Points */}
          {section.points && (
            <List sx={{ pl: 2, py: 1 }}>
              {section.points.map((point, i) => (
                <ListItem key={i} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={<span dangerouslySetInnerHTML={{ __html: formatText(point) }} />}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Ordered Steps */}
          {section.steps && (
            <List
              component="ol"
              sx={{
                pl: 4,
                py: 1,
                listStyleType: 'decimal',
                '& .MuiListItem-root': { display: 'list-item' },
              }}
            >
              {section.steps.map((step, i) => (
                <ListItem component="li" key={i} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={<span dangerouslySetInnerHTML={{ __html: formatText(step) }} />}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* Code Block */}
          {section.codeBlock && (
            <CodeBlock content={section.codeBlock.content} language={section.codeBlock.language} />
          )}

          {/* Section Note */}
          {section.note && (
            <Box
              mt={3}
              sx={{
                backgroundColor: '#f0f8ff',
                borderLeft: '5px solid #2196f3',
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderRadius: '8px',
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 500 }}
                dangerouslySetInnerHTML={{ __html: formatText(section.note) }}
              />
            </Box>
          )}
        </Box>
      ))}

      {/* Bottom Note (Global) */}
      {data.bottomNote && (
        <Box
          sx={{
            backgroundColor: '#f0f8ff',
            borderLeft: '5px solid #2196f3',
            display: 'flex',
            alignItems: 'center',
            p: 2,
            borderRadius: '8px',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#555' }}>
            <span dangerouslySetInnerHTML={{ __html: formatText(data.bottomNote) }} />
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const DocumentationContent = ({ selectedSection, onNavigate, onSearch, onBack, searchValue }) => {
  const [query, setQuery] = useState(searchValue);

  useEffect(() => {
    setQuery(searchValue);
  }, [searchValue]);

  const searchResults = useMemo(() => searchIndex(SEARCH_INDEX, query), [query]);
  const isSearching = Boolean(query);
  const isViewingSearchResult = isSearching && selectedSection && selectedSection !== 'about';

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    onSearch(value);
  };

  const dataToRender = DOCUMENTATION_DATA[selectedSection];

  const groupedResults = useMemo(() => {
    if (!isSearching) return {};

    return searchResults.reduce((acc, result) => {
      if (!acc[result.sectionKey]) {
        acc[result.sectionKey] = [];
      }
      acc[result.sectionKey].push(result);
      return acc;
    }, {});
  }, [isSearching, searchResults]);


  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 55,
          zIndex: 10,
          backgroundColor: 'white',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <TextField
          sx={{ width: '50%' }}
          placeholder="Search documentation..."
          value={query}
          onChange={handleSearchChange}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {isViewingSearchResult ? (
          <Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
              sx={{ mb: 2 }}
              variant="outlined"
            >
              Back to Search Results
            </Button>
            <DocumentationSection data={dataToRender} />
          </Box>
        ) : isSearching ? (
          searchResults.length ? (
            <Box>
              {Object.entries(groupedResults).map(([sectionKey, results]) => {
                const sectionData = DOCUMENTATION_DATA[sectionKey];

                return (
                  <Box
                    key={sectionKey}
                    onClick={() => onNavigate(sectionKey)}
                    sx={{
                      mb: 4,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      p: 2,
                      backgroundColor: '#fafafa',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f0f0f0',
                      },
                    }}
                  >

                    {/* Section title */}
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>

                      {sectionData?.title}
                    </Typography>

                    <List>
                      {results.map((result, index) => (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                dangerouslySetInnerHTML={{
                                  __html: formatText(result.content.slice(0, 160)) + '…',
                                }}
                              />
                            }
                          />

                        </ListItem>
                      ))}
                    </List>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography color="text.secondary">No results found.</Typography>
          )
        ) : (
          <DocumentationSection data={dataToRender} />
        )}

      </Box>
    </Box>
  );
};


export default DocumentationContent;
